import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { useLocation, useNavigate } from "react-router";
import i18n from "@/i18n";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";
import useAuthStore from "@/store/authStore";
import { toast } from "sonner";
import smrtMenuLogo from "@/assets/smrt-menu-logo.png";

interface GenericSideBarProps {
  menuItems: Array<{ id: string; path: string; label: string; icon: any }>;
}

const GenericSideBar: React.FC<GenericSideBarProps> = ({ menuItems }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useAuthStore((state) => state.logout);
  const {
    isMobile,
    toggleSidebar,
  } = useSidebar()

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/auth");
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error(i18n.t("auth.logoutError"));
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const handleSidebarMenuClick = (path: string) => {
    if (isActive(path)) {
      return;
    }
    navigate(path);    
    if (isMobile) {
      toggleSidebar();
    }
  }

  return (
    <Sidebar side="right">
      <SidebarHeader>
        <div className="flex items-center justify-center gap-2">
          <img src={smrtMenuLogo} alt="logo" className="w-48 h-48" />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <li
                  key={item.id}
                  className={`rounded-md cursor-pointer ${
                    isActive(item.path)
                      ? "bg-orange-200 dark:bg-gray-700"
                      : "hover:bg-orange-100 dark:hover:bg-gray-700"
                  }`}
                  onClick={() => handleSidebarMenuClick(item.path)}
                >
                  <a className="p-2 flex items-center gap-3 text-primaryText dark:text-gray-200">
                    <Icon
                      size={20}
                      className="text-primary dark:text-primary-dark"
                    />
                    <span>{i18n.t(item.label)}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <Button
          variant="destructive"
          className="w-full flex items-center gap-2 bg-white hover:bg-gray-100 text-red-600 hover:text-red-700 border border-red-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-red-400 dark:hover:text-red-300 dark:border-red-900"
          onClick={handleLogout}
        >
          <LogOut size={16} />
          <span>{i18n.t("sidebar.logout")}</span>
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
};

export default GenericSideBar;